import { startCase } from 'lodash';
import React from 'react';
import {
  BigKey,
  Card,
  CardTitle,
  SubtitleText,
} from 'spw-programming-projects/program-summary/components/styles';
import { Box, Grid } from '@mui/material';
import {
  AlignEndColumn,
  AlignStartColumn,
} from 'spw-shared/globalComponents/grid';
import {
  ISpaceBreakDown,
  UpdateSpacesByTypeParams,
} from 'spw-redux/modules/program-summary/types';
import { asPercentageRounded, asUsfOrZero } from 'spw-shared/utils';
import { PSCardGroupHeader, Row } from 'spw-shared/globalComponents';
import { BoldValue } from 'spw-programming-projects/program-summary/macro-single-page-summary/styles';
import {
  IProjectColors,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import {
  CUSTOM_SUBCATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  PROGRAMMING,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import { IProjectSpaceType } from 'spw-redux/modules/space_types/types';
import { useCustomSubcategory } from 'spw-esa-projects/program-summary/cards/hooks';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { UsfMixBar } from './usf-mix-bar';
import { SubcategoryContainer } from './styles';
import { useWorkspacesBreakdownBySubcategory } from '../../../../shared/hooks/use-workspaces-breakdown-by-subcategory';

const renderWorkspaceBreakdown = (
  viewOnly: boolean,
  totalUsf: number,
  onClickSpaceIcon: (e: Element) => void,
  projectColors: IProjectColors,
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[],
  projectSpaceTypes: { [id: number]: IProjectSpaceType },
  onUpdateAvgUsf?: (payload: UpdateSpacesByTypeParams) => void,
  clientFacingNamesAreVisible?: boolean
) => (workspaceBreakdown: ISpaceBreakDown) => {
  const {
    spaceTypeId,
    spaceType,
    category,
    name,
    usf,
    usfAvg,
    icon,
  } = workspaceBreakdown;

  return (
    <div data-testid={`bar-${spaceType}`} key={`div-bar-${spaceTypeId}`}>
      <UsfMixBar
        viewOnly={viewOnly}
        category={category}
        key={`bar-${spaceTypeId}`}
        spaceType={spaceType}
        spaceTypeId={spaceTypeId}
        spaceName={name}
        percentage={usf / totalUsf}
        usf={usf}
        usfAvg={usfAvg}
        icon={icon}
        onClickSpaceIcon={onClickSpaceIcon}
        projectColors={projectColors}
        projectSpaceTypeCategories={projectSpaceTypeCategories}
        projectSpaceTypes={projectSpaceTypes}
        onUpdateAvgUsf={onUpdateAvgUsf}
        clientFacingNamesAreVisible={clientFacingNamesAreVisible}
      />
    </div>
  );
};

export const WorkspaceUsfMix = ({
  projectType,
  workspacesBreakdown,
  title,
  onClickSpaceIcon,
  projectSpaceTypes,
  projectSpaceTypeCategories,
  projectColors,
  overrideContent,
  viewOnly,
  spaceTypeCategory,
  onUpdateAvgUsf,
  clientFacingNamesAreVisible,
}: {
  projectType?: string;
  workspacesBreakdown: ISpaceBreakDown[];
  title?: string;
  onClickSpaceIcon?: (e: Element) => void;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[];
  projectColors: IProjectColors;
  overrideContent?: JSX.Element;
  viewOnly: boolean;
  spaceTypeCategory: IProjectSpaceTypeCategory;
  onUpdateAvgUsf?: (payload: UpdateSpacesByTypeParams) => void;
  clientFacingNamesAreVisible?: boolean;
}) => {
  const { getUnit, convertArea } = useUnitConversion();
  const wsBreakdownBySubcategory = useWorkspacesBreakdownBySubcategory(
    spaceTypeCategory,
    workspacesBreakdown
  );

  const {
    customSubcategory,
    hasSpacesNotIncludedInTotalSeats,
  } = useCustomSubcategory(spaceTypeCategory, wsBreakdownBySubcategory);

  if (!workspacesBreakdown?.length && projectType === PROGRAMMING) {
    return null;
  }

  const totalUsf = Math.round(
    workspacesBreakdown.reduce(
      (total: number, spaceBreakDown: ISpaceBreakDown) => {
        return total + spaceBreakDown.usf;
      },
      0
    )
  );

  let content: JSX.Element;
  if (overrideContent) {
    content = overrideContent;
  } else if (
    spaceTypeCategory.defaultSpaceTypeCategory.category ===
    INDIVIDUAL_WORKSPACES_CATEGORY
  ) {
    content = (
      <>
        {[
          WORKSTATIONS_SUBCATEGORY,
          PRIVATE_OFFICES_SUBCATEGORY,
          CUSTOM_SUBCATEGORY,
        ].map((sc) => {
          if (!wsBreakdownBySubcategory[sc].length) return null;

          const subcategory =
            sc === CUSTOM_SUBCATEGORY
              ? customSubcategory
              : spaceTypeCategory.subcategories.find((spaceTypeSubcategory) => {
                  return (
                    spaceTypeSubcategory.defaultSpaceTypeCategory.category ===
                    sc
                  );
                });

          const subcategoryName = startCase(subcategory?.name ?? '');

          return (
            <SubcategoryContainer key={sc}>
              <PSCardGroupHeader>
                <Row>
                  <span>{subcategoryName}</span>
                </Row>
              </PSCardGroupHeader>
              {wsBreakdownBySubcategory[sc].map(
                renderWorkspaceBreakdown(
                  viewOnly,
                  totalUsf,
                  onClickSpaceIcon,
                  projectColors,
                  projectSpaceTypeCategories,
                  projectSpaceTypes,
                  onUpdateAvgUsf,
                  clientFacingNamesAreVisible
                )
              )}
            </SubcategoryContainer>
          );
        })}
      </>
    );
  } else {
    content = (
      <>
        {workspacesBreakdown.map(
          renderWorkspaceBreakdown(
            viewOnly,
            totalUsf,
            onClickSpaceIcon,
            projectColors,
            projectSpaceTypeCategories,
            projectSpaceTypes,
            onUpdateAvgUsf,
            clientFacingNamesAreVisible
          )
        )}
      </>
    );
  }

  return (
    <Card
      height="100%"
      boxSizing="border-box"
      style={{
        paddingBottom: hasSpacesNotIncludedInTotalSeats ? 46 : 16,
      }}
    >
      <Box>
        <CardTitle>{title}</CardTitle>
        <Grid container>
          <Grid item xs={6}>
            <AlignStartColumn marginTop="16px">
              <SubtitleText>{getUnit('area')} Totals</SubtitleText>
            </AlignStartColumn>
          </Grid>
          <Grid item xs={2}>
            <AlignEndColumn>
              <BoldValue data-testid="total_percentage">
                {asPercentageRounded(totalUsf / totalUsf, false)}
              </BoldValue>
              <BigKey>{getUnit('area')}</BigKey>
            </AlignEndColumn>
          </Grid>
          <Grid
            item
            xs={2}
            style={{ paddingBottom: '8px', paddingRight: '16px' }}
          >
            <AlignEndColumn>
              <BoldValue data-testid="total_usf">
                {asUsfOrZero(convertArea(totalUsf))}
              </BoldValue>
              <BigKey>{getUnit('area')}</BigKey>
            </AlignEndColumn>
          </Grid>
          <Grid
            item
            xs={2}
            style={{ borderLeft: '1px solid #E0E0E0', paddingBottom: '8px' }}
          />
        </Grid>
        <PSCardGroupHeader>
          <Row>
            <span>{spaceTypeCategory.name.toUpperCase()}</span>
          </Row>
        </PSCardGroupHeader>
        {content}
      </Box>
    </Card>
  );
};
